import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Package, Calculator, FileText, Euro, Calendar, User, Layers, Building, Briefcase, MapPin, Search, Network } from 'lucide-react';
import { useTheme } from '../../core/ui/ThemeContext';
import { useLanguage } from '../../core/i18n/LanguageContext';
import { quantityStep, sanitizeQuantity } from 'core/utils/units';
import MappingAutocomplete from '../common/MappingAutocomplete';
import useVendorNames from '../../core/ui/useVendorNames';

const GeneralTab = ({
  item,
  onUpdate,
  themeColors,
  units,
  hasLimitsData,
  hasServices,
  shouldHideKostenstelle,
  calculateEffectivePrice,
  costCenterName,
  plantName,
  purchasingOrgName,
  purchasingGroupName,
  user,
  validationErrors
}) => {
  const { isDarkMode } = useTheme();
  const { t } = useLanguage();
  // LIFNR -> Klartextname unter dem Wunschlieferanten-Feld (gemeinsamer Hook mit SourceTab)
  const vendorNameFor = useVendorNames([item.preferred_vendor], user?.token);

  // Helper: prüft ob ein Feld einen Validierungsfehler hat
  const hasFieldError = (fieldLabel) => {
    return validationErrors && validationErrors.includes(fieldLabel);
  };

  // CSS-Klasse für fehlerhafte Felder (roter Rahmen)
  const errorBorderClass = (fieldLabel) => {
    if (!hasFieldError(fieldLabel)) return '';
    return isDarkMode
      ? 'ring-2 ring-red-500/70 border-red-500/50'
      : 'ring-2 ring-red-400 border-red-300';
  };

  // Autocomplete states for plant field
  const [plantSearchResults, setPlantSearchResults] = useState([]);
  const [showPlantDropdown, setShowPlantDropdown] = useState(false);
  const [isSearchingPlant, setIsSearchingPlant] = useState(false);
  const plantInputRef = useRef(null);
  const plantDropdownRef = useRef(null);
  const plantSearchTimeout = useRef(null);

  // Autocomplete states for cost center field

  // Autocomplete states for purchasing organization field
  const [purchasingOrgSearchResults, setPurchasingOrgSearchResults] = useState([]);
  const [showPurchasingOrgDropdown, setShowPurchasingOrgDropdown] = useState(false);
  const [isSearchingPurchasingOrg, setIsSearchingPurchasingOrg] = useState(false);
  const purchasingOrgInputRef = useRef(null);
  const purchasingOrgDropdownRef = useRef(null);
  const purchasingOrgSearchTimeout = useRef(null);

  // Autocomplete states for purchasing group field
  const [purchasingGroupSearchResults, setPurchasingGroupSearchResults] = useState([]);
  const [showPurchasingGroupDropdown, setShowPurchasingGroupDropdown] = useState(false);
  const [isSearchingPurchasingGroup, setIsSearchingPurchasingGroup] = useState(false);
  const purchasingGroupInputRef = useRef(null);
  const purchasingGroupDropdownRef = useRef(null);
  const purchasingGroupSearchTimeout = useRef(null);

  // Debounced search for plant
  const searchPlant = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setPlantSearchResults([]);
      setShowPlantDropdown(false);
      return;
    }

    setIsSearchingPlant(true);
    try {
      const response = await fetch(`/api/admin/mappings/plant/search?q=${encodeURIComponent(query)}&limit=10`, {
        headers: {
          'Authorization': `Bearer ${user?.token}`
        }
      });

      if (response.ok) {
        const results = await response.json();
        setPlantSearchResults(results);
        setShowPlantDropdown(results.length > 0);
      }
    } catch (error) {
      console.error('Error searching plants:', error);
    } finally {
      setIsSearchingPlant(false);
    }
  }, [user?.token]);

  // Debounced search for purchasing organization
  const searchPurchasingOrg = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setPurchasingOrgSearchResults([]);
      setShowPurchasingOrgDropdown(false);
      return;
    }

    setIsSearchingPurchasingOrg(true);
    try {
      const response = await fetch(`/api/admin/mappings/purchasing_org/search?q=${encodeURIComponent(query)}&limit=10`, {
        headers: {
          'Authorization': `Bearer ${user?.token}`
        }
      });

      if (response.ok) {
        const results = await response.json();
        setPurchasingOrgSearchResults(results);
        setShowPurchasingOrgDropdown(results.length > 0);
      }
    } catch (error) {
      console.error('Error searching purchasing organizations:', error);
    } finally {
      setIsSearchingPurchasingOrg(false);
    }
  }, [user?.token]);

  // Debounced search for purchasing group
  const searchPurchasingGroup = useCallback(async (query) => {
    if (!query || query.length < 1) {
      setPurchasingGroupSearchResults([]);
      setShowPurchasingGroupDropdown(false);
      return;
    }

    setIsSearchingPurchasingGroup(true);
    try {
      const response = await fetch(`/api/admin/mappings/purchasing_group/search?q=${encodeURIComponent(query)}&limit=10`, {
        headers: {
          'Authorization': `Bearer ${user?.token}`
        }
      });

      if (response.ok) {
        const results = await response.json();
        setPurchasingGroupSearchResults(results);
        setShowPurchasingGroupDropdown(results.length > 0);
      }
    } catch (error) {
      console.error('Error searching purchasing groups:', error);
    } finally {
      setIsSearchingPurchasingGroup(false);
    }
  }, [user?.token]);

  // Handle plant input change with debounce
  const handlePlantInputChange = (value) => {
    onUpdate(item.id, 'plant', value);

    // Clear previous timeout
    if (plantSearchTimeout.current) {
      clearTimeout(plantSearchTimeout.current);
    }

    // Set new timeout for debounced search
    plantSearchTimeout.current = setTimeout(() => {
      searchPlant(value);
    }, 300);
  };

  // Handle purchasing organization input change with debounce
  const handlePurchasingOrgInputChange = (value) => {
    onUpdate(item.id, 'einkaueferorganisation', value);

    // Clear previous timeout
    if (purchasingOrgSearchTimeout.current) {
      clearTimeout(purchasingOrgSearchTimeout.current);
    }

    // Set new timeout for debounced search
    purchasingOrgSearchTimeout.current = setTimeout(() => {
      searchPurchasingOrg(value);
    }, 300);
  };

  // Handle purchasing group input change with debounce
  const handlePurchasingGroupInputChange = (value) => {
    onUpdate(item.id, 'einkauefergruppe', value);

    // Clear previous timeout
    if (purchasingGroupSearchTimeout.current) {
      clearTimeout(purchasingGroupSearchTimeout.current);
    }

    // Set new timeout for debounced search
    purchasingGroupSearchTimeout.current = setTimeout(() => {
      searchPurchasingGroup(value);
    }, 300);
  };

  // Handle plant selection from dropdown
  const handlePlantSelect = (result) => {
    onUpdate(item.id, 'plant', result.code);
    setShowPlantDropdown(false);
    setPlantSearchResults([]);
  };

  // Handle purchasing organization selection from dropdown
  const handlePurchasingOrgSelect = (result) => {
    onUpdate(item.id, 'einkaueferorganisation', result.code);
    setShowPurchasingOrgDropdown(false);
    setPurchasingOrgSearchResults([]);
  };

  // Handle purchasing group selection from dropdown
  const handlePurchasingGroupSelect = (result) => {
    onUpdate(item.id, 'einkauefergruppe', result.code);
    setShowPurchasingGroupDropdown(false);
    setPurchasingGroupSearchResults([]);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (plantDropdownRef.current && !plantDropdownRef.current.contains(event.target) &&
          plantInputRef.current && !plantInputRef.current.contains(event.target)) {
        setShowPlantDropdown(false);
      }
      if (purchasingOrgDropdownRef.current && !purchasingOrgDropdownRef.current.contains(event.target) &&
          purchasingOrgInputRef.current && !purchasingOrgInputRef.current.contains(event.target)) {
        setShowPurchasingOrgDropdown(false);
      }
      if (purchasingGroupDropdownRef.current && !purchasingGroupDropdownRef.current.contains(event.target) &&
          purchasingGroupInputRef.current && !purchasingGroupInputRef.current.contains(event.target)) {
        setShowPurchasingGroupDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (plantSearchTimeout.current) {
        clearTimeout(plantSearchTimeout.current);
      }
      if (purchasingOrgSearchTimeout.current) {
        clearTimeout(purchasingOrgSearchTimeout.current);
      }
      if (purchasingGroupSearchTimeout.current) {
        clearTimeout(purchasingGroupSearchTimeout.current);
      }
    };
  }, []);
  
  // Check if quantity and unit fields should be disabled
  const shouldDisableQuantityFields = () => {
    const isRelevantType = item.kontierungstyp === 'kostenstelle' || 
                           item.kontierungstyp === 'auftrag' || 
                           item.kontierungstyp === 'material';
    const hasServicesOrLimits = hasServices() || hasLimitsData();
    return isRelevantType && hasServicesOrLimits;
  };

  return (
    <div>
      {/* Hinweis wenn Limits oder Leistungen gesetzt sind */}
      {(hasLimitsData() || hasServices()) && (
        <div className={`mb-4 p-3 rounded-xl ${isDarkMode ? 'bg-orange-900/20 border border-orange-400/30' : 'bg-orange-100 border border-orange-300'}`}>
          <p className={`text-sm ${isDarkMode ? 'text-orange-200' : 'text-orange-800'} flex items-center`}>
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
            {hasLimitsData() && hasServices()
              ? (item.kontierungstyp === 'auftrag' ? t('banf.general.costCenterHiddenServicesAuftrag') : t('banf.general.costCenterHiddenServices'))
              : hasLimitsData()
              ? t('banf.general.costCenterLimitsControlled')
              : (item.kontierungstyp === 'auftrag' ? t('banf.general.costCenterHiddenLimitsAuftrag') : t('banf.general.costCenterHiddenLimits'))}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* Kontierung (Kostenstelle) - nur anzeigen wenn weder Limits noch Leistungen vorhanden sind und kontierungstyp kostenstelle ist */}
        {!shouldHideKostenstelle() && item.kontierungstyp === 'kostenstelle' && (
          <div className="relative">
            <label className={`block text-sm font-medium ${hasFieldError('Kostenstelle') ? (isDarkMode ? 'text-red-400' : 'text-red-600') : (isDarkMode ? 'text-white/90' : 'text-slate-700')} mb-2 flex items-center drop-shadow-sm`}>
              <Building className="w-4 h-4 mr-1.5" />
              {t('banf.general.kontierung')}
              {costCenterName && (
                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-green-900/50 text-green-300' : 'bg-slate-100 text-slate-600'}`}>
                  {costCenterName}
                </span>
              )}
            </label>
            {/* Kostenstelle live aus SAP (CSKS/CSKT, aktuell gueltig) statt Snapshot-Suche —
                konsistent mit Auftrag/PSP/Sachkonto. minChars 0 (CSKS ist klein). Freie Eingabe
                bleibt moeglich (SAP-Ausfall darf nicht blockieren). */}
            <MappingAutocomplete
              helpId="cost_center"
              context={{ kokrs: '0010' }}
              minChars={0}
              value={item.cost_center}
              onChange={(v) => onUpdate(item.id, 'cost_center', v)}
              placeholder={t('banf.general.searchCostCenter')}
              token={user?.token}
              isDarkMode={isDarkMode}
              errorBorderClass={errorBorderClass('Kostenstelle')}
            />
          </div>
        )}

        {/* Auftrag - nur anzeigen wenn weder Limits noch Leistungen vorhanden sind und kontierungstyp auftrag ist */}
        {!shouldHideKostenstelle() && item.kontierungstyp === 'auftrag' && (
          <div>
            <label className={`block text-sm font-medium ${hasFieldError('Auftrag') ? (isDarkMode ? 'text-red-400' : 'text-red-600') : (isDarkMode ? 'text-white/90' : 'text-slate-700')} mb-2 flex items-center drop-shadow-sm`}>
              <FileText className="w-4 h-4 mr-1.5" />
              {t('banf.general.order')}
            </label>
            {/* Innenauftrag live aus SAP (nur offene), statt aus dem 20-Codes-Seed. Suche ueber
                die Auftragsnummer; minChars 2 (86.000 offene Auftraege). */}
            <MappingAutocomplete
              helpId="order"
              context={{ kokrs: '0010' }}
              minChars={2}
              value={item.auftrag}
              onChange={(code) => onUpdate(item.id, 'auftrag', code)}
              placeholder={t('banf.general.orderPlaceholder')}
              isDarkMode={isDarkMode}
              token={user?.token}
              errorBorderClass={errorBorderClass('Auftrag')}
            />
          </div>
        )}

        {/* PSP-Element - nur bei kontierungstyp psp, wenn weder Limits noch Leistungen */}
        {!shouldHideKostenstelle() && item.kontierungstyp === 'psp' && (
          <div>
            <label className={`block text-sm font-medium ${hasFieldError('PSP-Element') ? (isDarkMode ? 'text-red-400' : 'text-red-600') : (isDarkMode ? 'text-white/90' : 'text-slate-700')} mb-2 flex items-center drop-shadow-sm`}>
              <Network className="w-4 h-4 mr-1.5" />
              {t('banf.general.psp')}
            </label>
            {/* PSP-Element live aus SAP, nur kontierbare (BELKZ). Suche ueber den externen
                Projektschluessel; minChars 2. Gebucht wird genau dieser Schluessel als
                EBKN.PS_PSP_PNR. */}
            <MappingAutocomplete
              helpId="wbs"
              context={{ kokrs: '0010' }}
              minChars={2}
              value={item.ps_psp_pnr || ''}
              onChange={(code) => onUpdate(item.id, 'ps_psp_pnr', code)}
              placeholder={t('banf.general.pspPlaceholder')}
              isDarkMode={isDarkMode}
              token={user?.token}
              errorBorderClass={errorBorderClass('PSP-Element')}
            />
          </div>
        )}

        {/* Kostenstelle - nur anzeigen bei Auftrag-Typ, wenn weder Limits noch Leistungen vorhanden sind */}
        {!shouldHideKostenstelle() && item.kontierungstyp === 'auftrag' && (
          <div className="relative">
            <label className={`block text-sm font-medium ${hasFieldError('Kostenstelle') ? (isDarkMode ? 'text-red-400' : 'text-red-600') : (isDarkMode ? 'text-white/90' : 'text-slate-700')} mb-2 flex items-center drop-shadow-sm`}>
              <Building className="w-4 h-4 mr-1.5" />
              {t('banf.general.costCenter')}
              {costCenterName && (
                <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-purple-900/50 text-purple-300' : 'bg-slate-100 text-slate-600'}`}>
                  {costCenterName}
                </span>
              )}
            </label>
            {/* Kostenstelle live aus SAP (CSKS/CSKT) — wie oben, hier fuer den Auftrag-Typ. */}
            <MappingAutocomplete
              helpId="cost_center"
              context={{ kokrs: '0010' }}
              minChars={0}
              value={item.cost_center}
              onChange={(v) => onUpdate(item.id, 'cost_center', v)}
              placeholder={t('banf.general.searchCostCenter')}
              token={user?.token}
              isDarkMode={isDarkMode}
              errorBorderClass={errorBorderClass('Kostenstelle')}
            />
          </div>
        )}

        {/* Sachkonto - nur anzeigen wenn keine Limits gesetzt sind und kontierungstyp nicht "material" ist */}
        {!hasLimitsData() && item.kontierungstyp !== 'material' && (
          <div>
            <label className={`block text-sm font-medium ${hasFieldError('Sachkonto') ? (isDarkMode ? 'text-red-400' : 'text-red-600') : (isDarkMode ? 'text-white/90' : 'text-slate-700')} mb-2 flex items-center drop-shadow-sm`}>
              <Calculator className="w-4 h-4 mr-1.5" />
              {t('banf.general.glAccount')}
            </label>
            {/* Wertehilfe mit Kontierungstyp als Kontext: bei gesetztem KNTTP (K/F) darf nur ein
                primaeres Kostenelement gewaehlt werden, sonst weist SAP die Kontierung ab. Der
                Kontext reduziert die Auswahl von 16.099 Sachkonten auf ~383 und ist zugleich
                fachlich korrekt. Freie Eingabe bleibt moeglich (SAP-Ausfall darf nicht
                blockieren). knttp wird aus dem UI-Kontierungstyp abgeleitet; dieses Feld ist
                ohnehin nur bei kostenstelle/auftrag sichtbar (Lager blendet es aus). */}
            <MappingAutocomplete
              helpId="gl_account"
              context={{ knttp: item.kontierungstyp === 'auftrag' ? 'F' : 'K' }}
              minChars={0}
              value={item.gl_account || ''}
              onChange={(v) => onUpdate(item.id, 'gl_account', v)}
              placeholder={t('banf.general.glAccountPlaceholder')}
              token={user?.token}
              isDarkMode={isDarkMode}
              errorBorderClass={errorBorderClass('Sachkonto')}
            />
          </div>
        )}

        {/* Material */}
        <div>
          <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-slate-700'} mb-2 flex items-center drop-shadow-sm`}>
            <Package className="w-4 h-4 mr-1.5" />
            {t('banf.general.material')}
          </label>
          {/* Material-Wertehilfe mit Werkskontext. Sucht nur MATNR (nummernbasiert) und liefert
              NUR im Werk beschaffbare Materialien — filtert also EOL/ohne-Einkaufssicht heraus.
              Genau an diesen drei Faellen ist im Schreibpfad-Test wiederholt etwas gescheitert.
              minChars=2, weil ohne Suchbegriff Tausende Materialien zurueckkaemen. Freie Eingabe
              bleibt moeglich (SAP-Ausfall darf nicht blockieren). */}
          <MappingAutocomplete
            helpId="material"
            context={{ werks: item.plant }}
            minChars={2}
            value={item.material || ''}
            onChange={(v) => onUpdate(item.id, 'material', v)}
            onSelect={(row) => {
              // SAP-Standard (ME51N): Materialauswahl zieht den Kurztext (MAKT.MAKTX) nach —
              // die Wertehilfe liefert ihn als row.name bereits mit. Bewusst ueberschreiben:
              // die Auswahl ist eine explizite Nutzeraktion; das Feld bleibt danach editierbar.
              if (row.name) onUpdate(item.id, 'description', row.name);
              // Mengeneinheit + Warengruppe kommen in SAP ebenfalls aus dem Materialstamm.
              // Die Wertehilfe-Zeile traegt sie noch nicht (Adapter-Anforderung an Konstantin:
              // meins/matkl in value-help material ergaenzen) — sobald sie da sind, greifen sie.
              if (row.meins) onUpdate(item.id, 'unit', row.meins);
              if (row.matkl) onUpdate(item.id, 'product_group', row.matkl);
            }}
            placeholder={t('banf.general.materialPlaceholder')}
            token={user?.token}
            isDarkMode={isDarkMode}
          />
        </div>

        {/* Kurztext */}
        <div className="lg:col-span-2">
          <label className={`block text-sm font-medium ${hasFieldError('Kurztext') ? (isDarkMode ? 'text-red-400' : 'text-red-600') : (isDarkMode ? 'text-white/90' : 'text-slate-700')} mb-2 flex items-center drop-shadow-sm`}>
            <FileText className="w-4 h-4 mr-1.5" />
            {t('banf.general.shortText')}
          </label>
          <div className="relative">
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]' : 'bg-white border border-slate-200'} rounded-2xl ${errorBorderClass('Kurztext')}`}></div>
            <input
              type="text"
              value={item.description}
              onChange={(e) => onUpdate(item.id, 'description', e.target.value)}
              className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${isDarkMode ? themeColors.focusRing : 'focus:ring-brand/40'} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-slate-800 placeholder-slate-400'}`}
              placeholder={t('banf.general.shortTextPlaceholder')}
              required
            />
          </div>
        </div>

        {/* Menge */}
        <div>
          <label className={`block text-sm font-medium ${hasFieldError('Menge') ? (isDarkMode ? 'text-red-400' : 'text-red-600') : (isDarkMode ? 'text-white/90' : 'text-slate-700')} mb-2 flex items-center drop-shadow-sm`}>
            <Calculator className="w-4 h-4 mr-1.5" />
            {t('banf.general.quantity')}
            {shouldDisableQuantityFields() && (
              <span className={`ml-2 text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-orange-900/50 text-orange-200' : 'bg-orange-100 text-orange-700'}`}>
                {t('banf.general.quantityFixed')}
              </span>
            )}
          </label>
          <div className="relative">
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]' : 'bg-white border border-slate-200'} rounded-2xl ${shouldDisableQuantityFields() ? 'opacity-60' : ''} ${errorBorderClass('Menge')}`}></div>
            <input
              type="number"
              min={quantityStep(item.unit)}
              step={quantityStep(item.unit)}
              value={shouldDisableQuantityFields() ? 1 : item.quantity}
              onChange={(e) => shouldDisableQuantityFields() ? null : onUpdate(item.id, 'quantity', sanitizeQuantity(e.target.value, item.unit))}
              disabled={shouldDisableQuantityFields()}
              className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${isDarkMode ? themeColors.focusRing : 'focus:ring-brand/40'} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-slate-800 placeholder-slate-400'} ${shouldDisableQuantityFields() ? 'cursor-not-allowed opacity-70' : ''}`}
              required
            />
          </div>
        </div>

        {/* Mengeneinheit */}
        <div>
          <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-slate-700'} mb-2 flex items-center drop-shadow-sm`}>
            <Layers className="w-4 h-4 mr-1.5" />
            {t('banf.general.unitOfMeasure')}
            {shouldDisableQuantityFields() && (
              <span className={`ml-2 text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-orange-900/50 text-orange-200' : 'bg-orange-100 text-orange-700'}`}>
                {t('banf.general.unitFixed')}
              </span>
            )}
          </label>
          <div className="relative">
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]' : 'bg-white border border-slate-200'} rounded-2xl ${shouldDisableQuantityFields() ? 'opacity-60' : ''}`}></div>
            <select
              value={shouldDisableQuantityFields() ? 'LE' : item.unit}
              onChange={(e) => {
                if (shouldDisableQuantityFields()) return;
                const newUnit = e.target.value;
                onUpdate(item.id, 'unit', newUnit);
                const roundedQty = sanitizeQuantity(item.quantity, newUnit);
                if (`${roundedQty}` !== `${item.quantity}`) onUpdate(item.id, 'quantity', roundedQty);
              }}
              disabled={shouldDisableQuantityFields()}
              className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${isDarkMode ? themeColors.focusRing : 'focus:ring-brand/40'} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white' : 'text-slate-800'} ${shouldDisableQuantityFields() ? 'cursor-not-allowed opacity-70' : ''}`}
            >
              {/* Der aktuelle Wert kann aus einem alten Entwurf oder OCI-Import stammen und nicht
                  in der (jetzt SAP-basierten) Liste sein — dann trotzdem als Option fuehren,
                  sonst zeigt das Select leer und der Nutzer verliert den Wert unbemerkt. */}
              {item.unit && !units.includes(item.unit) && (
                <option key={item.unit} value={item.unit} className={isDarkMode ? "bg-gray-800 text-white" : "bg-white text-slate-800"}>{item.unit}</option>
              )}
              {units.map(unit => (
                <option key={unit} value={unit} className={isDarkMode ? "bg-gray-800 text-white" : "bg-white text-slate-800"}>{unit}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Lieferdatum */}
        <div>
          <label className={`block text-sm font-medium ${hasFieldError('Lieferdatum') ? (isDarkMode ? 'text-red-400' : 'text-red-600') : (isDarkMode ? 'text-white/90' : 'text-slate-700')} mb-2 flex items-center drop-shadow-sm`}>
            <Calendar className="w-4 h-4 mr-1.5" />
            {t('banf.general.deliveryDate')}
          </label>
          <div className="relative">
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]' : 'bg-white border border-slate-200'} rounded-2xl ${errorBorderClass('Lieferdatum')}`}></div>
            <input
              type="date"
              value={item.delivery_date}
              onChange={(e) => onUpdate(item.id, 'delivery_date', e.target.value)}
              className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${isDarkMode ? themeColors.focusRing : 'focus:ring-brand/40'} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white' : 'text-slate-800'}`}
              required
            />
          </div>
        </div>

        {/* Warengruppe */}
        <div>
          <label className={`block text-sm font-medium ${hasFieldError('Warengruppe') ? (isDarkMode ? 'text-red-400' : 'text-red-600') : (isDarkMode ? 'text-white/90' : 'text-slate-700')} mb-2 flex items-center drop-shadow-sm`}>
            <Layers className="w-4 h-4 mr-1.5" />
            {t('banf.general.productGroup')}
          </label>
          <MappingAutocomplete
            type="material_group"
            value={item.product_group}
            onChange={(v) => onUpdate(item.id, 'product_group', v)}
            placeholder={t('banf.general.productGroupPlaceholder')}
            token={user?.token}
            isDarkMode={isDarkMode}
            errorBorderClass={errorBorderClass('Warengruppe')}
          />
        </div>

        {/* Werk - with Autocomplete */}
        <div className="relative">
          <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-slate-700'} mb-2 flex items-center drop-shadow-sm`}>
            <Building className="w-4 h-4 mr-1.5" />
            {t('banf.general.plant')}
            {plantName && (
              <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-emerald-900/50 text-emerald-300' : 'bg-slate-100 text-slate-600'}`}>
                {plantName}
              </span>
            )}
          </label>
          <div className="relative">
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]' : 'bg-white border border-slate-200'} rounded-2xl`}></div>
            <div className="relative flex items-center">
              <input
                ref={plantInputRef}
                type="text"
                value={item.plant}
                onChange={(e) => handlePlantInputChange(e.target.value)}
                onFocus={() => item.plant && searchPlant(item.plant)}
                className={`relative w-full px-3 py-2 pr-8 bg-transparent border-0 rounded-2xl focus:ring-2 ${isDarkMode ? themeColors.focusRing : 'focus:ring-brand/40'} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-slate-800 placeholder-slate-400'}`}
                placeholder={t('banf.general.searchPlant')}
                autoComplete="off"
                required
              />
              {isSearchingPlant ? (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className={`w-4 h-4 border-2 ${isDarkMode ? 'border-white/30 border-t-white' : 'border-slate-300 border-t-slate-600'} rounded-full animate-spin`}></div>
                </div>
              ) : (
                <Search className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
              )}
            </div>

            {/* Autocomplete Dropdown */}
            {showPlantDropdown && plantSearchResults.length > 0 && (
              <div
                ref={plantDropdownRef}
                className={`absolute z-50 w-full mt-1 rounded-xl overflow-hidden shadow-lg ${
                  isDarkMode
                    ? 'bg-gray-800/95 border border-white/20 backdrop-blur-xl'
                    : 'bg-white border border-slate-200'
                }`}
              >
                {plantSearchResults.map((result, index) => (
                  <button
                    key={`${result.code}-${index}`}
                    type="button"
                    onClick={() => handlePlantSelect(result)}
                    className={`w-full px-4 py-3 text-left transition-all duration-150 flex items-center justify-between ${
                      isDarkMode
                        ? 'hover:bg-emerald-600/30 text-white border-b border-white/10 last:border-b-0'
                        : 'hover:bg-slate-50 text-slate-800 border-b border-slate-100 last:border-b-0'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className={`font-mono font-semibold ${isDarkMode ? 'text-emerald-400' : 'text-brand'}`}>
                        {result.code}
                      </span>
                      <span className={`${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                        {result.name}
                      </span>
                    </div>
                    <Building className={`w-4 h-4 ${isDarkMode ? 'text-emerald-400/50' : 'text-slate-400'}`} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Lagerort - bei Material, Kostenstelle oder Auftrag anzeigen */}
        {(item.kontierungstyp === 'material' || item.kontierungstyp === 'kostenstelle' || item.kontierungstyp === 'auftrag') && (
          <div>
            <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-slate-700'} mb-2 flex items-center drop-shadow-sm`}>
              <MapPin className="w-4 h-4 mr-1.5" />
              {t('banf.general.storageLocation')}
            </label>
            {/* Lagerort ist WERKSABHAENGIG: die Wertehilfe bekommt das Werk als Kontext.
                Ohne diesen Kontext bekam bisher jeder Nutzer die Lagerorte von Werk 1000 —
                und SAP lehnte die Position dann ab. minChars=0: die Liste ist auch ohne
                Eingabe sinnvoll (ein Werk hat nur eine Handvoll Lagerorte). */}
            <MappingAutocomplete
              helpId="storage_location"
              context={{ werks: item.plant }}
              minChars={0}
              value={item.storage_location || ''}
              onChange={(v) => onUpdate(item.id, 'storage_location', v)}
              placeholder={t('banf.general.storageLocationPlaceholder')}
              token={user?.token}
              isDarkMode={isDarkMode}
            />
          </div>
        )}

        {/* Wunschlieferant — gleiche Mechanik wie im Bezugsquelle-Tab: LFA1-Wertehilfe +
            Klartextname dezent unter dem Feld (Wunsch Taras 28.07.) */}
        <div>
          <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-slate-700'} mb-2 flex items-center drop-shadow-sm`}>
            <User className="w-4 h-4 mr-1.5" />
            {t('banf.general.preferredVendor')}
          </label>
          <MappingAutocomplete
            helpId="vendor"
            minChars={2}
            value={item.preferred_vendor || ''}
            onChange={(v) => onUpdate(item.id, 'preferred_vendor', v)}
            placeholder={t('banf.general.preferredVendorPlaceholder')}
            token={user?.token}
            isDarkMode={isDarkMode}
          />
          {vendorNameFor(item.preferred_vendor) && (
            <p className={`mt-1 text-xs ${isDarkMode ? 'text-white/60' : 'text-slate-500'}`}>
              {vendorNameFor(item.preferred_vendor)}
            </p>
          )}
        </div>

        {/* Geschätzter Preis */}
        <div>
          <label className={`block text-sm font-medium ${hasFieldError('Geschätzter Preis') ? (isDarkMode ? 'text-red-400' : 'text-red-600') : (isDarkMode ? 'text-white/90' : 'text-slate-700')} mb-2 flex items-center drop-shadow-sm`}>
            <Euro className="w-4 h-4 mr-1.5" />
            {t('banf.general.estimatedPrice')}
            {(item.expected_value || (item.services && item.services.length > 0)) && (
              <span className={`ml-2 text-xs px-2 py-1 rounded-full ${isDarkMode ? 'bg-orange-900/50 text-orange-200' : 'bg-orange-100 text-orange-700'}`}>
                {t('banf.general.autoCalculated')}
              </span>
            )}
          </label>
          <div className="relative">
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]' : 'bg-white border border-slate-200'} rounded-2xl ${(item.expected_value || (item.services && item.services.length > 0)) ? 'opacity-60' : ''} ${errorBorderClass('Geschätzter Preis')}`}></div>
            <input
              type="number"
              min="0"
              step="0.01"
              value={item.expected_value || (item.services && item.services.length > 0) ? calculateEffectivePrice() : (item.price || '')}
              onChange={(e) => (!item.expected_value && (!item.services || item.services.length === 0)) ? onUpdate(item.id, 'price', e.target.value) : null}
              disabled={item.expected_value || (item.services && item.services.length > 0)}
              className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${isDarkMode ? themeColors.focusRing : 'focus:ring-brand/40'} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-slate-800 placeholder-slate-400'} ${(item.expected_value || (item.services && item.services.length > 0)) ? 'cursor-not-allowed opacity-70' : ''} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
              placeholder="0.00"
            />
          </div>
          {item.expected_value && (
            <p className={`text-xs ${isDarkMode ? 'text-orange-400' : 'text-orange-600'} mt-1`}>
              {t('banf.general.expectedValueHint')}
              {item.services && item.services.length > 0 && (' ' + (item.services.length !== 1 ? t('banf.general.plusServicePositionsPlural', { count: item.services.length }) : t('banf.general.plusServicePositions', { count: item.services.length })))}
            </p>
          )}
          {!item.expected_value && item.services && item.services.length > 0 && (
            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-slate-500'} mt-1`}>
              {t('banf.general.sumFrom')}{item.services.length !== 1 ? t('banf.general.servicePositionsPlural', { count: item.services.length }) : t('banf.general.servicePositions', { count: item.services.length })}
            </p>
          )}
        </div>

        {/* Preiseinheit (PEINH) — Preis bezieht sich auf diese Menge; Wert = Menge × Preis / PEINH */}
        <div>
          <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-slate-700'} mb-2 flex items-center drop-shadow-sm`}>
            <Calculator className="w-4 h-4 mr-1.5" />
            {t('banf.general.priceUnit')}
          </label>
          <div className="relative">
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]' : 'bg-white border border-slate-200'} rounded-2xl`}></div>
            <input
              type="number"
              min="1"
              step="1"
              value={item.price_unit ?? 1}
              onChange={(e) => onUpdate(item.id, 'price_unit', e.target.value)}
              className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${isDarkMode ? themeColors.focusRing : 'focus:ring-brand/40'} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-slate-800 placeholder-slate-400'} [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
              placeholder="1"
            />
          </div>
          <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-slate-500'} mt-1`}>
            {t('banf.general.priceUnitHint')}
          </p>
        </div>

        {/* Anforderer */}
        <div>
          <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-slate-700'} mb-2 flex items-center drop-shadow-sm`}>
            <User className="w-4 h-4 mr-1.5" />
            {t('banf.general.requester')}
          </label>
          <div className="relative">
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]' : 'bg-white border border-slate-200'} rounded-2xl`}></div>
            <input
              type="text"
              value={item.anforderer}
              onChange={(e) => onUpdate(item.id, 'anforderer', e.target.value)}
              className={`relative w-full px-3 py-2 bg-transparent border-0 rounded-2xl focus:ring-2 ${isDarkMode ? themeColors.focusRing : 'focus:ring-brand/40'} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-slate-800 placeholder-slate-400'}`}
              placeholder={t('banf.general.requesterPlaceholder')}
            />
          </div>
        </div>

        {/* Einkäufergruppe - with Autocomplete */}
        <div className="relative">
          <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-slate-700'} mb-2 flex items-center drop-shadow-sm`}>
            <Briefcase className="w-4 h-4 mr-1.5" />
            {t('banf.general.purchasingGroup')}
            {purchasingGroupName && (
              <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-orange-900/50 text-orange-300' : 'bg-slate-100 text-slate-600'}`}>
                {purchasingGroupName}
              </span>
            )}
          </label>
          <div className="relative">
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]' : 'bg-white border border-slate-200'} rounded-2xl`}></div>
            <div className="relative flex items-center">
              <input
                ref={purchasingGroupInputRef}
                type="text"
                value={item.einkauefergruppe}
                onChange={(e) => handlePurchasingGroupInputChange(e.target.value)}
                onFocus={() => item.einkauefergruppe && searchPurchasingGroup(item.einkauefergruppe)}
                className={`relative w-full px-3 py-2 pr-8 bg-transparent border-0 rounded-2xl focus:ring-2 ${isDarkMode ? themeColors.focusRing : 'focus:ring-brand/40'} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-slate-800 placeholder-slate-400'}`}
                placeholder={t('banf.general.searchPurchasingGroup')}
                autoComplete="off"
                required
              />
              {isSearchingPurchasingGroup ? (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className={`w-4 h-4 border-2 ${isDarkMode ? 'border-white/30 border-t-white' : 'border-slate-300 border-t-slate-600'} rounded-full animate-spin`}></div>
                </div>
              ) : (
                <Search className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
              )}
            </div>

            {/* Autocomplete Dropdown */}
            {showPurchasingGroupDropdown && purchasingGroupSearchResults.length > 0 && (
              <div
                ref={purchasingGroupDropdownRef}
                className={`absolute z-50 w-full mt-1 rounded-xl overflow-hidden shadow-lg ${
                  isDarkMode
                    ? 'bg-gray-800/95 border border-white/20 backdrop-blur-xl'
                    : 'bg-white border border-slate-200'
                }`}
              >
                {purchasingGroupSearchResults.map((result, index) => (
                  <button
                    key={`${result.code}-${index}`}
                    type="button"
                    onClick={() => handlePurchasingGroupSelect(result)}
                    className={`w-full px-4 py-3 text-left transition-all duration-150 flex items-center justify-between ${
                      isDarkMode
                        ? 'hover:bg-orange-600/30 text-white border-b border-white/10 last:border-b-0'
                        : 'hover:bg-slate-50 text-slate-800 border-b border-slate-100 last:border-b-0'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className={`font-mono font-semibold ${isDarkMode ? 'text-orange-400' : 'text-brand'}`}>
                        {result.code}
                      </span>
                      <span className={`${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                        {result.name}
                      </span>
                    </div>
                    <Briefcase className={`w-4 h-4 ${isDarkMode ? 'text-orange-400/50' : 'text-slate-400'}`} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Einkäuferorganisation - with Autocomplete */}
        <div className="relative">
          <label className={`block text-sm font-medium ${isDarkMode ? 'text-white/90' : 'text-slate-700'} mb-2 flex items-center drop-shadow-sm`}>
            <Building className="w-4 h-4 mr-1.5" />
            {t('banf.general.purchasingOrg')}
            {purchasingOrgName && (
              <span className={`ml-2 text-xs px-2 py-0.5 rounded-full ${isDarkMode ? 'bg-violet-900/50 text-violet-300' : 'bg-slate-100 text-slate-600'}`}>
                {purchasingOrgName}
              </span>
            )}
          </label>
          <div className="relative">
            <div className={`absolute inset-0 ${isDarkMode ? 'bg-gray-800/30 border border-white/30 backdrop-blur-xl shadow-[inset_0_1px_0_rgba(255,255,255,0.2)]' : 'bg-white border border-slate-200'} rounded-2xl`}></div>
            <div className="relative flex items-center">
              <input
                ref={purchasingOrgInputRef}
                type="text"
                value={item.einkaueferorganisation}
                onChange={(e) => handlePurchasingOrgInputChange(e.target.value)}
                onFocus={() => item.einkaueferorganisation && searchPurchasingOrg(item.einkaueferorganisation)}
                className={`relative w-full px-3 py-2 pr-8 bg-transparent border-0 rounded-2xl focus:ring-2 ${isDarkMode ? themeColors.focusRing : 'focus:ring-brand/40'} focus:outline-none transition-all duration-300 ${isDarkMode ? 'text-white placeholder-white/60' : 'text-slate-800 placeholder-slate-400'}`}
                placeholder={t('banf.general.searchPurchasingOrg')}
                autoComplete="off"
              />
              {isSearchingPurchasingOrg ? (
                <div className="absolute right-3 top-1/2 -translate-y-1/2">
                  <div className={`w-4 h-4 border-2 ${isDarkMode ? 'border-white/30 border-t-white' : 'border-slate-300 border-t-slate-600'} rounded-full animate-spin`}></div>
                </div>
              ) : (
                <Search className={`absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 ${isDarkMode ? 'text-white/40' : 'text-slate-400'}`} />
              )}
            </div>

            {/* Autocomplete Dropdown */}
            {showPurchasingOrgDropdown && purchasingOrgSearchResults.length > 0 && (
              <div
                ref={purchasingOrgDropdownRef}
                className={`absolute z-50 w-full mt-1 rounded-xl overflow-hidden shadow-lg ${
                  isDarkMode
                    ? 'bg-gray-800/95 border border-white/20 backdrop-blur-xl'
                    : 'bg-white border border-slate-200'
                }`}
              >
                {purchasingOrgSearchResults.map((result, index) => (
                  <button
                    key={`${result.code}-${index}`}
                    type="button"
                    onClick={() => handlePurchasingOrgSelect(result)}
                    className={`w-full px-4 py-3 text-left transition-all duration-150 flex items-center justify-between ${
                      isDarkMode
                        ? 'hover:bg-violet-600/30 text-white border-b border-white/10 last:border-b-0'
                        : 'hover:bg-slate-50 text-slate-800 border-b border-slate-100 last:border-b-0'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className={`font-mono font-semibold ${isDarkMode ? 'text-violet-400' : 'text-brand'}`}>
                        {result.code}
                      </span>
                      <span className={`${isDarkMode ? 'text-white/70' : 'text-slate-600'}`}>
                        {result.name}
                      </span>
                    </div>
                    <Building className={`w-4 h-4 ${isDarkMode ? 'text-violet-400/50' : 'text-slate-400'}`} />
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GeneralTab;